<template>
	<Flyout
		title="Edit Field Settings"
		parentTag="form"
		:display="display"
		@show="closeEditFieldsFlyout"
		:novalidate="'novalidate'"
	>
		<template #flyout-content>
			<div class="edit-form-builder">
				<FieldDetail
					v-model="dataFlyout"
					:impactList="impactList"
					:isEditObject="originalValue"
					@selected="getSnippetSelected"
					:showAddCodeDetails="false"
					:snippet="snippetCodeSelected"
					:rangeValues="rangeValues"
					:setRowFocus="display"
				/>

				<CodeDetails
					:snippet="snippetCodeSelected"
					v-if="showCodeDetails"
				/>

				<div class="edit-form-builder-code__edit-btn">
					<Button
						v-show="canBeEditCode"
						id="editCodeSnippet"
						labelText="Edit Code"
						:btnClick="handlerEditCodeSnippet"
						className="btn--small"
					/>
				</div>

				<CodeNewCopyModal
					v-model="showNewCodeModal"
					@show="closeCodeSelection"
				/>
			</div>
		</template>
		<template #flyout-footer>
			<ButtonGroup
				:items="[
					{
						className: 'btn--primary',
						labelText: 'Save Changes',
						btnClick: saveAction,
						disabled: modelHasError
					},
					{
						className: 'btn--link',
						labelText: 'Cancel',
						btnClick: cancelAction
					}
				]"
			/>
		</template>
	</Flyout>
</template>

<script>
import Flyout from '@/components/shells/flyout/flyout';
import ButtonGroup from '@/components/forms/button/button-group';
import FieldDetail from '@/views/form-designer/field-detail';
import CodeDetails from '@/views/form-designer/code-details';
import Button from '@/components/forms/button/button';
import { deepEqual, isEmpty } from '@/utils/object';
import { mapGetters } from 'vuex';
import CodeNewCopyModal from '@/views/code-manager/new-copy-of-code-settings-modal.vue';
import _ from 'lodash';

export default {
	name: 'FieldEditFlyout',
	components: {
		Flyout,
		ButtonGroup,
		FieldDetail,
		CodeDetails,
		Button,
		CodeNewCopyModal
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		fieldData: {
			type: Object,
			required: true
		},
		impactInputFieldsList: {
			type: Array
		},
		snippet: {
			type: Object,
			required: () => {}
		}
	},
	data() {
		return {
			dataFlyout: {},
			impactList: [],
			originalValue: {},
			snippetCodeSelected: {},
			showNewCodeModal: false,

			rangeValues: {
				characterNumber: { minValue: 1, maxValue: 50 },
				dot: { minValue: 0, maxValue: 11 },
				rowCol: { minValue: 0 }
			}
		};
	},
	computed: {
		...mapGetters('codemanager', ['codeSelected']),
		showCodeDetails() {
			return (
				!isEmpty(this.snippetCodeSelected) &&
				this.dataFlyout?.impactType === 'C'
			);
		},
		hasUpdatePermission() {
			return this.validatePermission('forms.update');
		},
		modelHasError() {
			const compareObj = ignoreProps => {
				return deepEqual(
					this.dataFlyout,
					this.fieldData,
					true,
					ignoreProps
				);
			};

			const areEqual = compareObj(['canBeUpdated', 'canBeDeleted']);
			if (_.isEmpty(this.dataFlyout) || areEqual) {
				return true;
			}
			const { row, dot, col } = this.dataFlyout;

			if (
				row === undefined ||
				row === '' ||
				row < _.get(this.rangeValues, 'rowCol.minValue', 0)
			) {
				return true;
			}
			if (
				dot === undefined ||
				dot === '' ||
				dot < _.get(this.rangeValues, 'dot.minValue', 0) ||
				dot > _.get(this.rangeValues, 'dot.maxValue', 11)
			) {
				return true;
			}
			if (
				col === undefined ||
				col === '' ||
				col < _.get(this.rangeValues, 'rowCol.minValue', 0)
			) {
				return true;
			}

			if (this.dataFlyout.impactType === 'S') {
				return !(
					this.dataFlyout.staticValue && this.dataFlyout.description
				);
			}
			if (this.dataFlyout.impactType === 'I') {
				return !this.dataFlyout.keyboardPrompt;
			}
			if (this.dataFlyout.impactType === 'C') {
				if (this.dataFlyout.truncate) {
					return (
						!this.dataFlyout.description ||
						this.dataFlyout.characterNumber <
							_.get(
								this.rangeValues,
								'characterNumber.minValue',
								1
							) ||
						this.dataFlyout.characterNumber >
							_.get(
								this.rangeValues,
								'characterNumber.maxValue',
								50
							)
					);
				} else {
					const areEqual = compareObj([
						'canBeUpdated',
						'canBeDeleted',
						'characterNumber'
					]);
					return !this.dataFlyout.description || areEqual;
				}
			}
			return false;
		},
		canBeEditCode() {
			return (
				this.dataFlyout.canBeUpdated &&
				this.dataFlyout.impactType === 'C'
			);
		}
	},
	methods: {
		closeEditFieldsFlyout() {
			if (!this.validateBeforeClose) {
				this.cancelAction();
			}
		},
		cancelAction() {
			(this.dataFlyout = {}),
				(this.originalValue = {}),
				(this.snippetCodeSelected = {}),
				this.$store.dispatch('codemanager/resetSnippet', true);
			this.$emit('show', { display: false });
		},
		getSnippetSelected(val) {
			this.snippetCodeSelected = { ...val };
		},
		saveAction() {
			this.$emit('change', this.dataFlyout);
			this.cancelAction();
		},
		handlerEditCodeSnippet() {
			this.snippetCodeSelected.sourceOfSelection = 'FieldEditFlyout';
			this.$store.dispatch(
				'codemanager/codeSelected',
				this.snippetCodeSelected
			);
			this.showNewCodeModal = true;
		},
		closeCodeSelection() {
			this.snippetCodeSelected = { ...this.codeSelected };
			this.showNewCodeModal = false;
		}
	},
	watch: {
		display: {
			handler(val) {
				if (val) {
					this.dataFlyout = { ...this.fieldData };
					this.snippetCodeSelected = { ...this.snippet };
					this.originalValue = Object.assign(
						{},
						{
							row: this.fieldData.row,
							dot: this.fieldData.dot,
							col: this.fieldData.col
						}
					);
				}
			}
		},
		dataFlyout: {
			deep: true,
			immediate: true,
			handler() {
				if (
					!this.dataFlyout.impactType ||
					this.dataFlyout.impactType !== 'C'
				) {
					if (!isEmpty(this.snippetCodeSelected)) {
						this.snippetCodeSelected = {};
					}
				}
			}
		},
		impactInputFieldsList: {
			deep: true,
			immediate: true,
			handler(list) {
				if (list && list.length) {
					this.impactList = [...list];
				}
			}
		},
		codeSelected: {
			deep: true,
			handler(val) {
				if (this.dataFlyout.impactType === 'C') {
					const {
						id,
						description,
						snippetFormat,
						snippetJustify,
						snippetCalc
					} = val || {};
					Object.assign(this.dataFlyout, {
						formsSnippetsId: id,
						description: description,
						formsSnippetDescription: description,
						format: snippetFormat,
						justify: snippetJustify,
						calc: snippetCalc
					});
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.edit-form-builder {
	display: grid;
	gap: $gutter-lg;
}
</style>
